import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import axios from 'axios';
import '../custom.css';

const Sections = () => {
    const toast = useRef(null);
    const inputRef = useRef(null);

    const [sectionName, setSectionName] = useState('');
    const [sections, setSections] = useState([]);
    const [editingSection, setEditingSection] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');

    useEffect(() => {
        fetchSections();
    }, []);

    const fetchSections = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/sections');
            setSections(res.data);
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch sections' });
        }
    };

    const handleSave = async () => {
        try {
            let res;
            if (editingSection) {
                res = await axios.post('https://treatway.co.in/Counter-Backend/api/server_masters/save/sections', { id: editingSection.sectionId, name: sectionName });
            } else {
                res = await axios.post('https://treatway.co.in/Counter-Backend/api/server_masters/save/sections', { name: sectionName });
            }

            if (res.data.status === 'success') {
                toast.current.show({
                    severity: 'success',
                    summary: editingSection ? 'Updated' : 'Added',
                    detail: editingSection ? 'Section updated successfully' : 'Section added successfully'
                });
                setSectionName('');
                setEditingSection(null);
                fetchSections();
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: res.data.message || 'Failed to save Section'
                });
                setTimeout(() => inputRef.current?.focus(), 0);
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to save Section' });
        }
    };

    const confirmSave = () => {
        if (!sectionName.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Warning', detail: 'Section name is required' });
            return;
        }

        confirmDialog({
            message: editingSection ? 'Are you sure you want to update this section?' : 'Are you sure you want to add this section?',
            header: editingSection ? 'Confirm Update' : 'Confirm Add',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes',
            rejectLabel: 'No',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-secondary',
            accept: () => handleSave()
        });
    };

    const handleEdit = (rowData) => {
        setEditingSection(rowData);
        setSectionName(rowData.section_name);

        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 0);
    };

    const confirmCancel = () => {
        confirmDialog({
            message: 'Are you sure you want to cancel editing?',
            header: 'Confirm Cancel',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes',
            rejectLabel: 'No',
            acceptClassName: 'p-button-danger',
            rejectClassName: 'p-button-secondary',
            accept: () => {
                setEditingSection(null);
                setSectionName('');
            }
        });
    };

    const actionBodyTemplate = (rowData) => {
        return <Button icon="pi pi-pencil" className="p-button-rounded p-button-warning mr-2" onClick={() => handleEdit(rowData)} />;
    };

    return (
        <>
            <ConfirmDialog />
            <Toast ref={toast} />
            <h2 className="text-xl font-bold mb-4">Section</h2>

            <div className="card">
                {/* Add / Edit Form */}
                <div className="flex flex-wrap align-items-end gap-3 mb-4">
                    <InputText ref={inputRef} value={sectionName} onChange={(e) => setSectionName(e.target.value)} placeholder="Enter Section" style={{ width: '250px' }} />
                    <Button label={editingSection ? 'Update' : 'Save'} icon="pi pi-save" className="p-button-success" onClick={confirmSave} />
                    {editingSection && <Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={confirmCancel} />}
                </div>
            </div>

            <div className="card">
                <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
                    <div className="flex items-center ml-auto min-w-[250px] justify-end w-full sm:w-auto">
                        <span className="p-input-icon-left w-full">
                            <i className="pi pi-search"></i>
                            <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." className="p-inputtext-sm w-full" />
                        </span>
                    </div>
                </div>

                <DataTable
                    value={sections}
                    showGridlines
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                    tableStyle={{ minWidth: '50rem' }}
                    className="p-datatable-sm"
                    globalFilter={globalFilter}
                >
                    <Column field="sectionId" header="ID" />
                    <Column field="section_name" header="Section" />
                    <Column header="Edit" body={actionBodyTemplate} />
                </DataTable>
            </div>
        </>
    );
};

export default Sections;
